'use client'

import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { cn } from '@/lib/utils'

// Scale types for non-linear sliders
export type ScaleType = 'linear' | 'log' | 'sqrt' | 'quadratic' | 'priceOptimized' | 'areaOptimized' | 'saleOptimized'

// Scale transformation functions
const scaleTransforms = {
  linear: {
    // Value to slider position (0-1)
    toSlider: (value: number, min: number, max: number) => (value - min) / (max - min),
    // Slider position (0-1) to value
    toValue: (position: number, min: number, max: number) => min + position * (max - min),
  },
  log: {
    // Logarithmic scale - gives more resolution to lower values
    toSlider: (value: number, min: number, max: number) => {
      const minLog = Math.log(min + 1)
      const maxLog = Math.log(max + 1)
      return (Math.log(value + 1) - minLog) / (maxLog - minLog)
    },
    toValue: (position: number, min: number, max: number) => {
      const minLog = Math.log(min + 1)
      const maxLog = Math.log(max + 1)
      return Math.exp(minLog + position * (maxLog - minLog)) - 1
    },
  },
  sqrt: {
    // Square root scale - moderate emphasis on lower values
    toSlider: (value: number, min: number, max: number) => {
      const minSqrt = Math.sqrt(min)
      const maxSqrt = Math.sqrt(max)
      return (Math.sqrt(value) - minSqrt) / (maxSqrt - minSqrt)
    },
    toValue: (position: number, min: number, max: number) => {
      const minSqrt = Math.sqrt(min)
      const maxSqrt = Math.sqrt(max)
      const sqrt = minSqrt + position * (maxSqrt - minSqrt)
      return sqrt * sqrt
    },
  },
  quadratic: {
    // Quadratic scale - gives more resolution to higher values
    toSlider: (value: number, min: number, max: number) => {
      const normalized = (value - min) / (max - min)
      return Math.sqrt(normalized)
    },
    toValue: (position: number, min: number, max: number) => {
      return min + position * position * (max - min)
    },
  },
  // Optimized for Czech rental prices (0-80k CZK)
  // Popular range: 15k-35k CZK gets ~45% of slider space
  priceOptimized: {
    toSlider: (value: number, min: number, max: number) => {
      // Piecewise linear: expand 15k-35k range
      // 0-15k: 0-15% of slider
      // 15k-35k: 15-60% of slider (popular range gets 45%)
      // 35k-80k: 60-100% of slider
      if (value <= 15000) {
        return (value / 15000) * 0.15
      } else if (value <= 35000) {
        return 0.15 + ((value - 15000) / 20000) * 0.45
      } else {
        return 0.6 + ((value - 35000) / (max - 35000)) * 0.4
      }
    },
    toValue: (position: number, min: number, max: number) => {
      if (position <= 0.15) {
        return (position / 0.15) * 15000
      } else if (position <= 0.6) {
        return 15000 + ((position - 0.15) / 0.45) * 20000
      } else {
        return 35000 + ((position - 0.6) / 0.4) * (max - 35000)
      }
    },
  },
  // Optimized for Czech real-estate sale prices (0–50M CZK)
  // Popular market: 2M–10M gets ~50% of slider space
  saleOptimized: {
    toSlider: (value: number, min: number, max: number) => {
      const lo = min + (max - min) * 0.04   // 2M when max=50M
      const hi = min + (max - min) * 0.20   // 10M when max=50M
      if (value <= lo) {
        return (value / lo) * 0.15
      } else if (value <= hi) {
        return 0.15 + ((value - lo) / (hi - lo)) * 0.50
      } else {
        return 0.65 + ((value - hi) / (max - hi)) * 0.35
      }
    },
    toValue: (position: number, min: number, max: number) => {
      const lo = min + (max - min) * 0.04
      const hi = min + (max - min) * 0.20
      if (position <= 0.15) {
        return (position / 0.15) * lo
      } else if (position <= 0.65) {
        return lo + ((position - 0.15) / 0.50) * (hi - lo)
      } else {
        return hi + ((position - 0.65) / 0.35) * (max - hi)
      }
    },
  },
  // Optimized for Czech apartment areas (0-200 m²)
  // Popular range: 30-80 m² gets ~50% of slider space
  areaOptimized: {
    toSlider: (value: number, min: number, max: number) => {
      // Piecewise linear: expand 30-80 m² range
      // 0-30: 0-15% of slider
      // 30-80: 15-65% of slider (popular range gets 50%)
      // 80-200: 65-100% of slider
      if (value <= 30) {
        return (value / 30) * 0.15
      } else if (value <= 80) {
        return 0.15 + ((value - 30) / 50) * 0.5
      } else {
        return 0.65 + ((value - 80) / (max - 80)) * 0.35
      }
    },
    toValue: (position: number, min: number, max: number) => {
      if (position <= 0.15) {
        return (position / 0.15) * 30
      } else if (position <= 0.65) {
        return 30 + ((position - 0.15) / 0.5) * 50
      } else {
        return 80 + ((position - 0.65) / 0.35) * (max - 80)
      }
    },
  },
}

// Compact version for inline use
interface CompactRangeSliderProps {
  min: number
  max: number
  step?: number
  value: [number, number]
  onValueChange: (value: [number, number]) => void
  onValueCommit?: (value: [number, number]) => void
  formatValue?: (value: number) => string
  parseValue?: (input: string) => number // Parse user input back to number
  label?: string
  unit?: string
  className?: string
  marks?: number // Number of marks to show on the slider
  scale?: ScaleType // Scale type for non-linear distribution
  displayWhenDefault?: string // Text to show in pill when no filter is active (e.g. "Price")
  borderless?: boolean // Remove border from trigger button (for grouped layouts)
}

export function CompactRangeSlider({
  min,
  max,
  step = 1,
  value,
  onValueChange,
  onValueCommit,
  formatValue = (v) => (v === undefined || isNaN(v) ? '' : Math.round(v).toLocaleString()),
  parseValue,
  label,
  unit,
  className,
  marks = 3, // Default 3 marks for compact version
  scale = 'linear',
  displayWhenDefault,
  borderless,
}: CompactRangeSliderProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  // Get scale transform functions
  const scaleTransform = scaleTransforms[scale]

  // Convert actual values to slider values (0-100 range for internal slider)
  const sliderValue = React.useMemo((): [number, number] => {
    return [
      scaleTransform.toSlider(value[0], min, max) * 100,
      scaleTransform.toSlider(value[1], min, max) * 100,
    ]
  }, [value, min, max, scaleTransform])

  // Convert slider values back to actual values
  const handleSliderChange = (sliderVals: number[]) => {
    const actualMin = scaleTransform.toValue(sliderVals[0] / 100, min, max)
    const actualMax = scaleTransform.toValue(sliderVals[1] / 100, min, max)
    // Round to step
    const steppedMin = Math.round(actualMin / step) * step
    const steppedMax = Math.round(actualMax / step) * step
    onValueChange([
      Math.max(min, Math.min(steppedMin, max)),
      Math.max(min, Math.min(steppedMax, max)),
    ])
  }

  const handleSliderCommit = (sliderVals: number[]) => {
    const actualMin = scaleTransform.toValue(sliderVals[0] / 100, min, max)
    const actualMax = scaleTransform.toValue(sliderVals[1] / 100, min, max)
    const steppedMin = Math.round(actualMin / step) * step
    const steppedMax = Math.round(actualMax / step) * step
    onValueCommit?.([
      Math.max(min, Math.min(steppedMin, max)),
      Math.max(min, Math.min(steppedMax, max)),
    ])
  }
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [minInput, setMinInput] = React.useState(formatValue(value[0]))
  const [maxInput, setMaxInput] = React.useState(formatValue(value[1]))
  const [isEditingMin, setIsEditingMin] = React.useState(false)
  const [isEditingMax, setIsEditingMax] = React.useState(false)

  const hasValue = value[0] !== min || value[1] !== max

  // Build display text - always show full range for stable layout
  const displayText = React.useMemo(() => {
    const [minVal, maxVal] = value
    return `${formatValue(minVal)} – ${formatValue(maxVal)}${unit ? ` ${unit}` : ''}`
  }, [value, formatValue, unit])

  // Sync input values when slider value changes (and not editing)
  React.useEffect(() => {
    if (!isEditingMin) {
      setMinInput(formatValue(value[0]))
    }
  }, [value[0], formatValue, isEditingMin])

  React.useEffect(() => {
    if (!isEditingMax) {
      setMaxInput(formatValue(value[1]))
    }
  }, [value[1], formatValue, isEditingMax])

  // Default parse function - handles "k" suffix for thousands
  const defaultParseValue = (input: string): number => {
    const cleaned = input.replace(/[^0-9.k]/gi, '').toLowerCase()
    if (cleaned.endsWith('k')) {
      return parseFloat(cleaned.slice(0, -1)) * 1000
    }
    return parseFloat(cleaned) || 0
  }

  const parse = parseValue || defaultParseValue

  const handleMinBlur = () => {
    setIsEditingMin(false)
    const parsed = parse(minInput)
    const clamped = Math.round(Math.max(min, Math.min(parsed, value[1])))
    onValueChange([clamped, value[1]])
    onValueCommit?.([clamped, value[1]])
  }

  const handleMaxBlur = () => {
    setIsEditingMax(false)
    const parsed = parse(maxInput)
    const clamped = Math.round(Math.max(value[0], Math.min(parsed, max)))
    onValueChange([value[0], clamped])
    onValueCommit?.([value[0], clamped])
  }

  const handleMinKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleMinBlur()
      ;(e.target as HTMLInputElement).blur()
    }
  }

  const handleMaxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleMaxBlur()
      ;(e.target as HTMLInputElement).blur()
    }
  }

  // Marks evenly distributed in slider position space so they are visually equidistant.
  const markPositions = React.useMemo(() => {
    if (marks <= 0) return []
    const positions: Array<{ percent: number; value: number }> = []
    for (let i = 0; i < marks; i++) {
      const position = marks === 1 ? 0 : i / (marks - 1)
      const percent = position * 100
      const markValue = scaleTransform.toValue(position, min, max)
      positions.push({ percent, value: markValue })
    }
    return positions
  }, [marks, min, max, scaleTransform])

  // Close on outside click
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all',
          borderless
            ? ''
            : 'border-2',
          hasValue
            ? borderless
              ? 'text-primary'
              : 'bg-primary/10 text-primary border-primary'
            : borderless
              ? 'text-muted-foreground hover:text-foreground'
              : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
        )}
      >
        {label && <span className="text-xs font-medium opacity-70">{label}:</span>}
        <span className="inline-block text-right min-w-[80px]">{displayText}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 z-50 min-w-full w-max max-w-sm p-4 rounded-xl bg-popover border border-border shadow-lg">
          {/* Editable inputs */}
          <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-foreground mb-3">
            <input
              type="text"
              value={minInput}
              onChange={(e) => setMinInput(e.target.value)}
              onFocus={() => setIsEditingMin(true)}
              onBlur={handleMinBlur}
              onKeyDown={handleMinKeyDown}
              className={cn(
                'w-16 px-2 py-1 text-right rounded border border-border',
                'bg-muted/50 hover:bg-muted focus:bg-muted focus:border-primary',
                'focus:outline-none transition-colors'
              )}
            />
            <span className="text-muted-foreground">–</span>
            <input
              type="text"
              value={maxInput}
              onChange={(e) => setMaxInput(e.target.value)}
              onFocus={() => setIsEditingMax(true)}
              onBlur={handleMaxBlur}
              onKeyDown={handleMaxKeyDown}
              className={cn(
                'w-16 px-2 py-1 text-right rounded border border-border',
                'bg-muted/50 hover:bg-muted focus:bg-muted focus:border-primary',
                'focus:outline-none transition-colors'
              )}
            />
            {unit && <span className="text-muted-foreground ml-0.5">{unit}</span>}
          </div>

          <div className="relative">
            <SliderPrimitive.Root
              className="relative flex items-center select-none touch-none w-full h-5"
              min={0}
              max={100}
              step={0.1}
              value={sliderValue}
              onValueChange={handleSliderChange}
              onValueCommit={handleSliderCommit}
            >
              <SliderPrimitive.Track className="relative h-1.5 grow rounded-full bg-muted">
                <SliderPrimitive.Range className="absolute h-full rounded-full bg-primary" />
              </SliderPrimitive.Track>
              <SliderPrimitive.Thumb
                className={cn(
                  'block h-5 w-5 rounded-full bg-primary border-2 border-background',
                  'shadow-md shadow-black/20',
                  'ring-offset-background transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  'hover:scale-110 active:scale-95 transition-transform cursor-grab active:cursor-grabbing'
                )}
              />
              <SliderPrimitive.Thumb
                className={cn(
                  'block h-5 w-5 rounded-full bg-primary border-2 border-background',
                  'shadow-md shadow-black/20',
                  'ring-offset-background transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  'hover:scale-110 active:scale-95 transition-transform cursor-grab active:cursor-grabbing'
                )}
              />
            </SliderPrimitive.Root>

            {/* Tick marks */}
            {markPositions.length > 0 && (
              <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 pointer-events-none">
                {markPositions.map((mark, index) => (
                  <div
                    key={index}
                    className="absolute w-0.5 h-2 bg-muted-foreground/20"
                    style={{ left: `${mark.percent}%`, transform: 'translateX(-50%)' }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mark value labels */}
          <div className="relative mt-2 h-4">
            {markPositions.map((mark, index) => {
              const isFirst = index === 0
              const isLast = index === markPositions.length - 1

              return (
                <span
                  key={index}
                  className="absolute text-xs text-muted-foreground whitespace-nowrap"
                  style={{
                    left: `${mark.percent}%`,
                    transform: isFirst
                      ? 'none'
                      : isLast
                        ? 'translateX(-100%)'
                        : 'translateX(-50%)',
                  }}
                >
                  {formatValue(mark.value)}
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
